import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

// Attach token on every request
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('mh_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Auto-refresh on 401
api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = localStorage.getItem('mh_refresh');
        const { data } = await api.post('/auth/refresh-token', { refresh_token: refresh });
        localStorage.setItem('mh_token', data.data.access_token);
        original.headers.Authorization = `Bearer ${data.data.access_token}`;
        return api(original);
      } catch {
        localStorage.removeItem('mh_token');
        localStorage.removeItem('mh_refresh');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login:        (data) => api.post('/auth/login', data),
  me:           ()     => api.get('/auth/me'),
  logout:       ()     => api.post('/auth/logout'),
  refreshToken: (data) => api.post('/auth/refresh-token', data),
};

export const pagesAPI = {
  list:   (p) => api.get('/pages', { params: p }),
  get:    (id) => api.get(`/pages/${id}`),
  create: (d)  => api.post('/pages', d),
  update: (id, d) => api.put(`/pages/${id}`, d),
  remove: (id) => api.delete(`/pages/${id}`),
};

export const articlesAPI = {
  list:    (p)     => api.get('/articles', { params: p }),
  get:     (slug)  => api.get(`/articles/${slug}`),
  create:  (d)     => api.post('/articles', d),
  update:  (id, d) => api.put(`/articles/${id}`, d),
  remove:  (id)    => api.delete(`/articles/${id}`),
  publish: (id)    => api.put(`/articles/${id}/publish`),
};

export const servicesAPI = {
  categories: ()       => api.get('/services/categories'),
  list:       (p)      => api.get('/services', { params: p }),
  get:        (slug)   => api.get(`/services/${slug}`),
  create:     (d)      => api.post('/services', d),
  update:     (id, d)  => api.put(`/services/${id}`, d),
  remove:     (id)     => api.delete(`/services/${id}`),
};

export const ecosystemAPI = {
  list:   (p)      => api.get('/ecosystem', { params: p }),
  get:    (slug)   => api.get(`/ecosystem/${slug}`),
  create: (d)      => api.post('/ecosystem', d),
  update: (id, d)  => api.put(`/ecosystem/${id}`, d),
  remove: (id)     => api.delete(`/ecosystem/${id}`),
};

export const programsAPI = {
  domains: ()      => api.get('/programs/domains'),
  list:    (p)     => api.get('/programs', { params: p }),
  get:     (slug)  => api.get(`/programs/${slug}`),
  create:  (d)     => api.post('/programs', d),
  update:  (id, d) => api.put(`/programs/${id}`, d),
};

export const inboxAPI = {
  messages:      (p)      => api.get('/admin/contact', { params: p }),
  updateMessage: (id, d)  => api.put(`/contact/${id}/status`, d),
  applications:  (p)      => api.get('/admin/applications', { params: p }),
  updateApp:     (id, d)  => api.put(`/applications/${id}/status`, d),
};

export const donationsAPI = {
  packages: ()     => api.get('/support-packages'),
  list:     (p)    => api.get('/admin/donations', { params: p }),
  summary:  ()     => api.get('/admin/donations/summary'),
};

export const usersAPI = {
  list:   (p)      => api.get('/users', { params: p }),
  create: (d)      => api.post('/users', d),
  update: (id, d)  => api.put(`/users/${id}`, d),
  roles:  ()       => api.get('/roles'),
  logs:   (p)      => api.get('/admin/activity-logs', { params: p }),
};

export default api;

export const apiFetch = (path, options = {}) => {
  const baseURL = process.env.REACT_APP_API_URL || '/api/v1';
  const token = localStorage.getItem('mh_token');

  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  return fetch(`${baseURL}${path}`, {
    ...options,
    headers,
  });
};
